import { StickyNote, Navigation } from 'lucide-react';
import { portalColors, portalShadow, portalRadius } from './portalStyles';
import { ChecklistSection } from './ChecklistSection';
import { LifecycleSection } from './LifecycleSection';
import type { CrewPortalJob, CrewPortalSettings } from '../../types/crewPortal';

interface Props {
  job: CrewPortalJob;
  accentColor: string;
  label: string;
  showNavigate?: boolean;
  firstName: string;
  /** §K — when present (today's primary job), the card renders the two-way
   *  lifecycle (accept/thread/GPS/server checklist/complete/pay) instead of
   *  the v1 localStorage checklist. Absent → the v1 render is the fallback. */
  lifecycle?: {
    slug: string;
    settings: CrewPortalSettings;
    businessName: string;
    businessLogoUrl: string | null;
    onCompleted: () => void;
  };
}

function formatTime(time: string | null): string {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${ampm}`;
}

function formatDuration(mins: number | null): string {
  if (!mins) return '';
  if (mins < 60) return `${mins}min`;
  const hours = Math.round(mins / 60 * 10) / 10;
  return `~${hours}hr`;
}

function getMapUrl(job: CrewPortalJob): string | null {
  const loc = job.location;
  if (!loc) return null;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (loc.latitude && loc.longitude) {
    return isIOS
      ? `maps://maps.apple.com/?daddr=${loc.latitude},${loc.longitude}`
      : `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`;
  }
  const addr = [loc.address, loc.city, loc.province].filter(Boolean).join(', ');
  if (!addr) return null;
  return isIOS
    ? `maps://maps.apple.com/?daddr=${encodeURIComponent(addr)}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
}

export function JobHeroCard({ job, accentColor, label, showNavigate = true, firstName, lifecycle }: Props) {
  const locationName = job.location?.name ?? job.title;
  const address = job.location ? [job.location.address, job.location.city, job.location.province].filter(Boolean).join(', ') : null;
  const timeStr = formatTime(job.scheduled_time);
  const durStr = formatDuration(job.estimated_duration_minutes);
  // \u00A7K \u2014 the pay chip rides the time line when the sidecar carries pay_cents
  const payStr = lifecycle && job.state?.pay_cents != null
    ? `$${(job.state.pay_cents / 100).toFixed(job.state.pay_cents % 100 === 0 ? 0 : 2)} pay`
    : '';
  const timeLine = [timeStr, durStr, payStr].filter(Boolean).join(' \u00B7 ');
  const mapUrl = getMapUrl(job);
  const hasChecklist = job.checklist && job.checklist.length > 0;

  return (
    <div style={{
      background: portalColors.card, borderRadius: portalRadius.card,
      boxShadow: portalShadow.card, borderLeft: `4px solid ${accentColor}`,
      padding: 20,
    }}>
      {/* Label */}
      <div style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase',
        color: accentColor, marginBottom: 8,
      }}>{label}</div>

      {/* Location name */}
      <div style={{ fontSize: 20, fontWeight: 500, color: portalColors.primary }}>
        {locationName}
      </div>

      {/* Address */}
      {address && (
        <div style={{ fontSize: 14, color: portalColors.secondary, marginTop: 2 }}>{address}</div>
      )}

      {/* Time + duration */}
      {timeLine && (
        <div style={{ fontSize: 14, color: accentColor, marginTop: 8 }}>{timeLine}</div>
      )}

      {/* Navigate button */}
      {showNavigate && mapUrl && (
        <a
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', height: 48, borderRadius: portalRadius.button,
            background: accentColor, color: '#fff', textDecoration: 'none',
            fontSize: 15, fontWeight: 600, marginTop: 16,
          }}
        >
          <Navigation size={18} />
          Navigate
        </a>
      )}

      {/* Notes */}
      {job.notes && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          background: `${accentColor}0F`, borderRadius: 10,
          padding: '12px 14px', marginTop: 16,
        }}>
          <StickyNote size={16} color={accentColor} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: portalColors.secondary, lineHeight: '18px' }}>{job.notes}</span>
        </div>
      )}

      {/* §K two-way lifecycle (today's primary) — else the v1 checklist fallback */}
      {lifecycle ? (
        <LifecycleSection
          job={job}
          slug={lifecycle.slug}
          settings={lifecycle.settings}
          accentColor={accentColor}
          firstName={firstName}
          businessName={lifecycle.businessName}
          businessLogoUrl={lifecycle.businessLogoUrl}
          onCompleted={lifecycle.onCompleted}
        />
      ) : hasChecklist ? (
        <ChecklistSection
          checklist={job.checklist!}
          jobId={job.id}
          accentColor={accentColor}
          firstName={firstName}
        />
      ) : null}
    </div>
  );
}
